import { useRef, useState, useEffect } from "react";
import { useScrollReveal } from "../useScrollReveal";
import "./Es3Apply.css";

const CRAFT_OPTIONS = [
  { value: "food-beverage", label: "Food & Beverage" },
  { value: "painting",      label: "Painting or drawing" },
  { value: "pottery",       label: "Pottery or ceramics" },
  { value: "textiles",      label: "Textiles or stitching" },
  { value: "jewellery",     label: "Jewellery" },
  { value: "candles",       label: "Candles or fragrance" },
  { value: "prints",        label: "Prints or zines" },
  { value: "photography",   label: "Photography" },
  { value: "woodwork",      label: "Woodwork or small furniture" },
  { value: "upcycled",      label: "Painted vinyl or upcycled art" },
  { value: "other",         label: "Something else" },
];

const BEYOND = [
  { value: "workshop", label: "Host a workshop" },
  { value: "perform",  label: "Perform on the stage" },
  { value: "screen",   label: "Screen a film or visual work" },
  { value: "podcast",  label: "Record in the podcast corner" },
  { value: "sell",     label: "Just sell my work for now" },
];

const STEPS = ["You", "Your work", "The deal"];

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

function PhotoSlot({ index, slot, onSelect, onRemove }) {
  const inputRef = useRef(null);

  const state = !slot ? "empty"
    : slot.error     ? "error"
    : slot.uploading ? "uploading"
    : slot.url       ? "done"
    : "empty";

  return (
    <div
      className={`es3-photo-slot es3-photo-slot-${state}`}
      onClick={() => state === "empty" || state === "error" ? inputRef.current?.click() : undefined}
      title={slot?.file?.name || "Click to add photo"}
    >
      {state === "done" && (
        <>
          <img src={slot.preview} alt={`photo ${index + 1}`} className="es3-photo-slot-img" />
          <button type="button" className="es3-photo-slot-remove" onClick={(e) => { e.stopPropagation(); onRemove(index); }}>✕</button>
          <span className="es3-photo-slot-ok">✓</span>
        </>
      )}
      {state === "uploading" && (
        <>
          <img src={slot.preview} alt="" className="es3-photo-slot-img es3-photo-slot-dim" />
          <div className="es3-photo-slot-spinner" />
        </>
      )}
      {state === "error" && (
        <div className="es3-photo-slot-empty">
          <span className="es3-photo-slot-plus" style={{ color: "#e07070" }}>!</span>
          <span className="es3-photo-slot-label" style={{ color: "#e07070", fontSize: 10 }}>
            {slot.error}
          </span>
        </div>
      )}
      {state === "empty" && (
        <div className="es3-photo-slot-empty">
          <span className="es3-photo-slot-plus">+</span>
          <span className="es3-photo-slot-label">Add Photo</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => { if (e.target.files[0]) onSelect(index, e.target.files[0]); e.target.value = ""; }}
      />
    </div>
  );
}

function Es3Apply() {
  const { ref: headRef, fromDir: headFrom } = useScrollReveal(0.1);
  const { ref: formRef, fromDir: formFrom } = useScrollReveal(0.05);

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [beyond, setBeyond] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [photos, setPhotos] = useState(Array(6).fill(null));
  // Each slot: null | { file, preview, url, uploading, error }
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [error, setError] = useState("");
  const [stepError, setStepError] = useState("");

  const [fields, setFields] = useState({
    name: "", city: "", whatsapp: "", email: "", link: "",
    craft: "",
    description: "", story: "", quantity: "", price: "", restock: "",
    delivery: "", fragile: "",
  });

  const [foodFields, setFoodFields] = useState({
    foodOffering: "",
    foodDescription: "",
    foodMadeBy: "",
    foodDeliveryFresh: "",
    foodDeliveryNote: "",
    foodStorage: "",
    foodFreshDuration: "",
    foodDailyQty: "",
    foodPackaging: "",
    foodPrice: "",
    foodCertification: "",
    foodOther: "",
  });

  const setField = (key) => (e) => { setStepError(""); setFields((f) => ({ ...f, [key]: e.target.value })); };
  const setFoodField = (key) => (e) => { setStepError(""); setFoodFields((f) => ({ ...f, [key]: e.target.value })); };

  const apiBase = process.env.NODE_ENV === "production"
    ? window.location.origin
    : (process.env.REACT_APP_API_URL || "http://localhost:5000");

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lsKey = "es_apply_visit";
    if (localStorage.getItem(lsKey) === today) return; // already tracked today
    fetch(`${apiBase}/api/stats/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: "apply_visit" }),
    })
      .then(() => localStorage.setItem(lsKey, today))
      .catch(() => {});
  }, []); // eslint-disable-line

  const setPhoto = async (i, file) => {
    if (file.size > MAX_FILE_BYTES) {
      const next = [...photos];
      next[i] = { file, preview: null, url: null, uploading: false, error: "Over 10 MB" };
      setPhotos(next);
      return;
    }
    const preview = URL.createObjectURL(file);
    setPhotos((p) => { const n = [...p]; n[i] = { file, preview, url: null, uploading: true, error: null }; return n; });
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await fetch(`${apiBase}/api/uploads`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      setPhotos((p) => { const n = [...p]; n[i] = { file, preview, url: data.url, uploading: false, error: null }; return n; });
    } catch (err) {
      setPhotos((p) => { const n = [...p]; n[i] = { file, preview, url: null, uploading: false, error: "Failed — tap to retry" }; return n; });
    }
  };

  const removePhoto = (i) => {
    setPhotos((p) => { const n = [...p]; n[i] = null; return n; });
  };

  const toggleBeyond = (v) => setBeyond((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);

  const isFood = fields.craft === "food-beverage";

  const next = async () => {
    if (step === 0) {
      if (!fields.name.trim())  { setStepError("Name is required."); return; }
      if (!fields.city.trim())  { setStepError("City and area is required."); return; }
      if (!fields.email.trim()) { setStepError("Email is required."); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) { setStepError("That does not look like a valid email address. Please check it."); return; }
      if (fields.whatsapp && !/^03\d{9}$/.test(fields.whatsapp)) { setStepError("WhatsApp number must be 11 digits starting with 03 — e.g. 03001234567"); return; }

      setStepLoading(true);
      try {
        const res = await fetch(`${apiBase}/api/submissions/check-email?email=${encodeURIComponent(fields.email.trim())}`);
        const data = await res.json();
        if (data.exists) {
          setStepError("You have already submitted an application with this email. If you made a mistake, WhatsApp us directly.");
          setStepLoading(false);
          return;
        }
      } catch {
        // Network error — let them proceed, backend will catch it on final submit
      }
      setStepLoading(false);
    }
    if (step === 1) {
      if (!fields.craft) { setStepError("Please select what you make."); return; }
      if (isFood) {
        if (!foodFields.foodOffering)            { setStepError("Please select what you are offering."); return; }
        if (!foodFields.foodDescription.trim())  { setStepError("Please describe your product."); return; }
        if (!foodFields.foodMadeBy)              { setStepError("Please tell us if this is something you make yourself."); return; }
        if (!foodFields.foodDeliveryFresh)       { setStepError("Please let us know if you can deliver fresh daily."); return; }
        if (!foodFields.foodStorage)             { setStepError("Please select how you need to store your product."); return; }
        if (!foodFields.foodDailyQty.trim())     { setStepError("Please tell us your typical daily quantity."); return; }
        if (!foodFields.foodPackaging)           { setStepError("Please let us know about your packaging."); return; }
        if (!foodFields.foodPrice.trim())        { setStepError("Price range per item is required."); return; }
        if (!foodFields.foodCertification)       { setStepError("Please tell us about your food handling certification."); return; }
      } else {
        if (!fields.description.trim()) { setStepError("Please describe what you want to put in Exyst Space."); return; }
        if (!fields.story.trim())       { setStepError("Please share the story behind your work."); return; }
        if (!fields.quantity)           { setStepError("Please select how many finished pieces you have ready."); return; }
        if (!fields.price.trim())       { setStepError("Price range is required."); return; }
        if (!fields.restock)            { setStepError("Please let us know if you can restock."); return; }
      }
      const uploadingCount = photos.filter((p) => p?.uploading).length;
      if (uploadingCount > 0) { setStepError("Photos are still uploading. Please wait before continuing."); return; }
      const uploadedCount = photos.filter((p) => p?.url).length;
      if (uploadedCount === 0) { setStepError("Please add at least one photo of your work."); return; }
      const failCount = photos.filter((p) => p && p.error).length;
      if (failCount > 0) { setStepError("One or more photos failed. Tap the red slot to retry or remove it."); return; }
    }
    setStepError("");
    setStep((s) => Math.min(s + 1, 2));
  };

  const back = () => { setStepError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!fields.delivery) {
      setError("Please tell us how your work would reach DHA Phase 4.");
      setLoading(false);
      return;
    }
    if (!agreed) {
      setError("Please read and check the agreement before submitting.");
      setLoading(false);
      return;
    }

    const photoUrls = photos.filter((p) => p?.url).map((p) => p.url);

    const payload = {
      ...fields,
      crafts: JSON.stringify([fields.craft].filter(Boolean)),
      agreed: agreed,
      beyond: JSON.stringify(beyond),
      photoUrls: JSON.stringify(photoUrls),
      ...(isFood ? foodFields : {}),
    };

    try {
      const res = await fetch(`${apiBase}/api/submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        let msg = "Something went wrong on our end. Please try again in a moment.";
        try { const d = await res.json(); if (d.error) msg = d.error; } catch {}
        setError(msg);
      }
    } catch {
      setError("Could not reach the server. Check your connection and try again, or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="es3-section es3-apply" id="es3-apply">
        <div className="es3-apply-success">
          <div className="es3-hand" style={{ color: "var(--es3-tan)", marginBottom: 16 }}>received.</div>
          <p className="es3-apply-success-text">
            A human reads every single one of these. If your work is right for this drop, you hear from us on WhatsApp within 7 days. Nobody gets left on read here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="es3-section es3-apply" id="es3-apply">
      <div ref={headRef} style={headFrom(0)}>
        <div className="es3-hand">fill this space</div>
        <h2 className="es3-h2">The room is being built. <em>The walls are already waiting.</em></h2>
        <p className="es3-prose" style={{ marginTop: 16 }}>
          Ten minutes. Write the way you talk. We are not hiring you. We are hanging you.
        </p>
      </div>

      <div className="es3-step-row">
        {STEPS.map((label, i) => (
          <div key={i} className={`es3-step-item${i === step ? " active" : i < step ? " done" : ""}`}>
            <div className="es3-step-dot">{i < step ? "✓" : i + 1}</div>
            <span className="es3-step-label">{label}</span>
            {i < STEPS.length - 1 && <div className={`es3-step-line${i < step ? " done" : ""}`} />}
          </div>
        ))}
      </div>

      <form className="es3-apply-card" ref={formRef} style={formFrom(100)} onSubmit={submit}>

        {/* ── STEP 0: You ── */}
        {step === 0 && (
          <div className="es3-step-panel">
            <div className="es3-fs-head">Tell us who you are</div>
            <div className="es3-two">
              <div className="es3-field">
                <label className="es3-label" htmlFor="es3-name">Full name</label>
                <input className="es3-input" id="es3-name" placeholder="Your name"
                  value={fields.name} onChange={setField("name")} />
              </div>
              <div className="es3-field">
                <label className="es3-label" htmlFor="es3-city">City and area</label>
                <input className="es3-input" id="es3-city" placeholder="Lahore, Karachi, anywhere"
                  value={fields.city} onChange={setField("city")} />
              </div>
              <div className="es3-field">
                <label className="es3-label" htmlFor="es3-wa">WhatsApp <span className="es3-optional">(optional)</span></label>
                <input
                  className="es3-input"
                  id="es3-wa"
                  placeholder="03xx xxxxxxx"
                  inputMode="numeric"
                  value={fields.whatsapp}
                  onChange={(e) => { setStepError(""); setFields((f) => ({ ...f, whatsapp: e.target.value.replace(/\D/g, "").slice(0, 11) })); }}
                />
                <div className="es3-hint">11 digits starting with 03 — e.g. 03001234567. We do not call unannounced.</div>
              </div>
              <div className="es3-field">
                <label className="es3-label" htmlFor="es3-email">Email</label>
                <input className="es3-input" id="es3-email" type="email" placeholder="you@example.com"
                  value={fields.email} onChange={setField("email")} />
              </div>
            </div>
            <div className="es3-field">
              <label className="es3-label" htmlFor="es3-link">Instagram or any link to your work <span className="es3-optional">(optional)</span></label>
              <input className="es3-input" id="es3-link" placeholder="No account, no problem. The photos below matter more."
                value={fields.link} onChange={setField("link")} />
            </div>
            {stepError && <p className="es3-step-error">{stepError}</p>}
            <div className="es3-step-nav">
              <button type="button" className="es3-submit" onClick={next} disabled={stepLoading}>
                {stepLoading ? "Checking..." : "Continue →"}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 1: Your work ── */}
        {step === 1 && (
          <div className="es3-step-panel">
            <div className="es3-fs-head">Tell us what you make</div>

            <div className="es3-field">
              <label className="es3-label" htmlFor="es3-craft">What do you make?</label>
              <select className="es3-input es3-select" id="es3-craft"
                value={fields.craft} onChange={setField("craft")}>
                <option value="" disabled>Choose a category</option>
                {CRAFT_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* ── Food & Beverage card ── */}
            {isFood && (
              <div className="es3-food-card" key="food-card">
                <div className="es3-food-card-tag">Food & Beverage Vendor Form</div>

                {/* Row 1: offering + made by */}
                <div className="es3-two">
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-offering">What are you offering?</label>
                    <select className="es3-input es3-select" id="es3-food-offering"
                      value={foodFields.foodOffering} onChange={setFoodField("foodOffering")}>
                      <option value="" disabled>Choose</option>
                      <option>Desserts</option>
                      <option>Coffee & Drinks</option>
                      <option>Savoury</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-madeby">Is this something you make yourself?</label>
                    <select className="es3-input es3-select" id="es3-food-madeby"
                      value={foodFields.foodMadeBy} onChange={setFoodField("foodMadeBy")}>
                      <option value="" disabled>Choose</option>
                      <option>Yes, entirely</option>
                      <option>Made with a team</option>
                      <option>Sourced and curated by me</option>
                    </select>
                  </div>
                </div>

                {/* Full width: product description */}
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-food-desc">
                    Tell us what you make, what makes it yours, and why it belongs in a space like this
                  </label>
                  <textarea className="es3-input es3-textarea" id="es3-food-desc"
                    value={foodFields.foodDescription} onChange={setFoodField("foodDescription")}
                    placeholder="Your product, your process, what makes it different." />
                </div>

                {/* Row 2: fresh delivery + storage */}
                <div className="es3-two">
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-delivery">Can you deliver fresh daily?</label>
                    <select className="es3-input es3-select" id="es3-food-delivery"
                      value={foodFields.foodDeliveryFresh} onChange={setFoodField("foodDeliveryFresh")}>
                      <option value="" disabled>Choose</option>
                      <option>Yes</option>
                      <option>No</option>
                      <option>Depends on the arrangement</option>
                    </select>
                  </div>
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-storage">How do you need to store your product?</label>
                    <select className="es3-input es3-select" id="es3-food-storage"
                      value={foodFields.foodStorage} onChange={setFoodField("foodStorage")}>
                      <option value="" disabled>Choose</option>
                      <option>Room temperature</option>
                      <option>Refrigerated</option>
                      <option>I have my own refrigerator I'd like to place on-site</option>
                    </select>
                  </div>
                </div>

                {/* Conditional: delivery note */}
                {foodFields.foodDeliveryFresh === "Depends on the arrangement" && (
                  <div className="es3-field es3-food-subnote">
                    <label className="es3-label" htmlFor="es3-food-delivery-note">Tell us more about the arrangement</label>
                    <textarea className="es3-input es3-textarea" id="es3-food-delivery-note" style={{ minHeight: 80 }}
                      value={foodFields.foodDeliveryNote} onChange={setFoodField("foodDeliveryNote")}
                      placeholder="What works for you and what does not." />
                  </div>
                )}

                {/* Row 3: stays fresh + price per item */}
                <div className="es3-two">
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-fresh">How long does your product stay fresh once prepared?</label>
                    <input className="es3-input" id="es3-food-fresh"
                      value={foodFields.foodFreshDuration} onChange={setFoodField("foodFreshDuration")}
                      placeholder="e.g. 4 hours, same day, 2 days" />
                    <div className="es3-hint">Helps us plan display and rotation.</div>
                  </div>
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-price">Price range per item</label>
                    <input className="es3-input" id="es3-food-price"
                      value={foodFields.foodPrice} onChange={setFoodField("foodPrice")}
                      placeholder="Approximate, in rupees" />
                    <div className="es3-hint">Helps us understand fit.</div>
                  </div>
                </div>

                {/* Full width: daily quantity */}
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-food-qty">What does a typical daily quantity look like?</label>
                  <textarea className="es3-input es3-textarea" id="es3-food-qty" style={{ minHeight: 80 }}
                    value={foodFields.foodDailyQty} onChange={setFoodField("foodDailyQty")}
                    placeholder="How many units, portions, or batches can you bring per day?" />
                </div>

                {/* Row 4: packaging + certification */}
                <div className="es3-two">
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-pkg">Do you have packaging?</label>
                    <select className="es3-input es3-select" id="es3-food-pkg"
                      value={foodFields.foodPackaging} onChange={setFoodField("foodPackaging")}>
                      <option value="" disabled>Choose</option>
                      <option>Yes, branded</option>
                      <option>Yes, unbranded</option>
                      <option>No, open to suggestions</option>
                    </select>
                  </div>
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-food-cert">Food handling certificate?</label>
                    <select className="es3-input es3-select" id="es3-food-cert"
                      value={foodFields.foodCertification} onChange={setFoodField("foodCertification")}>
                      <option value="" disabled>Choose</option>
                      <option>Yes</option>
                      <option>No</option>
                      <option>In progress</option>
                    </select>
                  </div>
                </div>

                {/* Full width: anything else */}
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-food-other">
                    Anything else we should know about your product or process? <span className="es3-optional">(optional)</span>
                  </label>
                  <textarea className="es3-input es3-textarea" id="es3-food-other"
                    value={foodFields.foodOther} onChange={setFoodField("foodOther")}
                    placeholder="Sourcing, story, dietary considerations, anything that makes what you do different." />
                </div>
              </div>
            )}

            {/* ── Product vendor fields ── */}
            {!isFood && fields.craft && (
              <>
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-desc">Describe what you want to put in Exyst Space</label>
                  <textarea className="es3-input es3-textarea" id="es3-desc" placeholder="What is it, what is it made of, what makes it yours."
                    value={fields.description} onChange={setField("description")} />
                </div>
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-story">The story behind it</label>
                  <textarea className="es3-input es3-textarea" id="es3-story" placeholder="Why do you make this? Who taught you? What does it mean to you? Write it real."
                    value={fields.story} onChange={setField("story")} />
                  <div className="es3-hint">This answer goes on the gallery label beside your work if you are selected.</div>
                </div>
              </>
            )}

            {/* Photos -- shown for both vendor types once a category is selected */}
            {fields.craft && (
              <div className="es3-field">
                <label className="es3-label">3 to 6 photos of your work</label>
                <div className="es3-hint" style={{ marginBottom: 14 }}>Phone photos are fine. Natural light, plain background if you can manage it.</div>
                <div className="es3-photo-grid">
                  {photos.map((slot, i) => (
                    <PhotoSlot key={i} index={i} slot={slot} onSelect={setPhoto} onRemove={removePhoto} />
                  ))}
                </div>
              </div>
            )}

            {/* Product-only: quantity / price / restock */}
            {!isFood && fields.craft && (
              <>
                <div className="es3-two">
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-qty">Finished pieces ready right now</label>
                    <select className="es3-input es3-select" id="es3-qty"
                      value={fields.quantity} onChange={setField("quantity")}>
                      <option value="" disabled>Choose</option>
                      <option>1 to 5</option>
                      <option>6 to 15</option>
                      <option>16 to 40</option>
                      <option>More than 40</option>
                    </select>
                  </div>
                  <div className="es3-field">
                    <label className="es3-label" htmlFor="es3-price">Price range you imagine</label>
                    <input className="es3-input" id="es3-price" placeholder="Your honest number, in rupees"
                      value={fields.price} onChange={setField("price")} />
                    <div className="es3-hint">We help you price for a gallery wall, usually higher than you think.</div>
                  </div>
                </div>
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-restock">If your pieces sell, can you make more?</label>
                  <select className="es3-input es3-select" id="es3-restock"
                    value={fields.restock} onChange={setField("restock")}>
                    <option value="" disabled>Choose</option>
                    <option>Yes, easily</option>
                    <option>Yes, but slowly</option>
                    <option>No, these are one-offs</option>
                  </select>
                </div>
              </>
            )}

            {stepError && <p className="es3-step-error">{stepError}</p>}
            <div className="es3-step-nav">
              <button type="button" className="es3-submit es3-submit-ghost" onClick={back}>← Back</button>
              <button type="button" className="es3-submit" onClick={next}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Getting it here + agreement ── */}
        {step === 2 && (
          <div className="es3-step-panel">
            <div className="es3-fs-head">Getting it to us</div>
            <div className="es3-two">
              <div className="es3-field">
                <label className="es3-label" htmlFor="es3-ship">
                  {isFood ? "How would your products reach DHA Phase 4, Lahore?" : "How would your work reach DHA Phase 4, Lahore?"}
                </label>
                <select className="es3-input es3-select" id="es3-ship"
                  value={fields.delivery} onChange={setField("delivery")}>
                  <option value="" disabled>Choose</option>
                  <option>I can drop it off myself</option>
                  <option>I can courier it</option>
                  <option>I need help arranging transport</option>
                </select>
              </div>
              {!isFood && (
                <div className="es3-field">
                  <label className="es3-label" htmlFor="es3-fragile">Fragile or needs special handling? <span className="es3-optional">(optional)</span></label>
                  <input className="es3-input" id="es3-fragile" placeholder="Tell us how to treat it"
                    value={fields.fragile} onChange={setField("fragile")} />
                </div>
              )}
            </div>

            <div className="es3-fs-head">Beyond the shelf</div>
            <div className="es3-fs-sub">The stage and studio run on the same principle as the shelves.</div>
            <div className="es3-field">
              <div className="es3-checks">
                {BEYOND.map((b) => (
                  <label key={b.value} className={`es3-chk${beyond.includes(b.value) ? " checked" : ""}`}>
                    <input type="checkbox" checked={beyond.includes(b.value)} onChange={() => toggleBeyond(b.value)} />
                    {b.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="es3-fs-head">The understanding</div>
            <label className={`es3-agree${agreed ? " checked" : ""}`}>
              <input type="checkbox" required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>I understand that Exyst Space is curated, so not every submission is displayed. I understand that sales work on a revenue share shared with me in writing before anything of mine goes on display, and that I keep the majority of every sale. I give Exyst Space permission to photograph my displayed work for the space's social media, always with my name credited.</span>
            </label>

            {error && <p style={{ color: "#e07070", fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{error}</p>}
            <div className="es3-step-nav">
              <button type="button" className="es3-submit es3-submit-ghost" onClick={back} disabled={loading}>← Back</button>
              <button type="submit" className="es3-submit" disabled={loading}>
                {loading ? "Sending..." : "Put me on the wall"}
              </button>
            </div>
            <p className="es3-fine">A human reads every single one of these. If your work is right for this drop, you hear from us on WhatsApp within 7 days.</p>
          </div>
        )}

      </form>
    </section>
  );
}

export default Es3Apply;
